import { FC, useEffect, useState } from 'react';
import { useInterval } from '@trukt/react-hooks/useInterval';
import { CircleAlert, CircleCheck, X } from '../../../../../components/icons';
import ProgressBar from '../../../../../components/ProgressBar';
import { Typography } from '../../../../../components/Typography';
import { useColor } from '../../../../../hooks/useColor';

import { ProgressNotifierItemProps } from './ProgressNotifierItem.types';

const noop = () => {};

export const ProgressNotifierItem: FC<ProgressNotifierItemProps> = ({ data, onRemove = noop }) => {
  const [count, setCount] = useState(0);
  const successColor = useColor({ color: 'success' });
  const whiteColor = useColor({ color: 'white' });

  useInterval(() => {
    if (data.isSuccess || data.isError) return;

    setCount((currentCount) => {
      if (currentCount >= 100) return currentCount;
      if (currentCount < 90 || data.isWarning) return currentCount + 1;
      return currentCount;
    });
  }, 20);

  const displayedCount = data.isSuccess || data.isError ? 100 : count;

  useEffect(() => {
    if (!data.isSuccess) return undefined;
    const timeoutId = setTimeout(() => onRemove(data.id), 1000);

    return () => clearTimeout(timeoutId);
  }, [data.id, data.isSuccess, onRemove]);

  const handleGetCurrentColorStatus = () => {
    if (data.isError) return '!text-error-400';
    if (data.isWarning) return '!text-warning-400';
    return data.isSuccess ? '!text-success-400' : '!text-primary-400';
  };

  const handleGetCurrentBgColor = () => {
    if (data.isError) return '!bg-error-400';
    if (data.isWarning) return '!bg-warning-400';
    return data.isSuccess ? '!bg-success-400' : '!bg-primary-400';
  };

  const handleGetCurrentFillStatus = () => {
    if (data.isError) return '!fill-error-400';
    if (data.isWarning) return '!fill-warning-400';
    return data.isSuccess ? '!fill-success-400' : '!fill-primary-400';
  };

  const colorStatus = handleGetCurrentColorStatus();

  return (
    <div
      role="progressbar"
      data-testid="progress__notifier__item"
      className={[
        'pointer-events-auto bg-bg-secondary shadow-primary-outset',
        'rounded-lg p-2',
      ].join(' ')}
    >
      <div className="mb-2 flex items-center text-left">
        <Typography variant="h6" className="flex-[1]" disableEllipse>
          {data.label}
        </Typography>

        <span data-testid="remove__button__wrapper">
          <X
            role="img"
            aria-label="Close Notification"
            size="1.2rem"
            onClick={() => onRemove(data.id)}
            className={['cursor-pointer', colorStatus].join(' ')}
            fill={successColor}
          >
            <title>Close Notification</title>
          </X>
        </span>
      </div>

      {displayedCount === 100 ? (
        <>
          <div className="flex items-center justify-end gap-2 text-right">
            <Typography className={[colorStatus, 'whitespace-pre-wrap'].join(' ')}>
              {(() => {
                if (data.isSuccess) return 'Complete';
                if (data.isWarning) return data.warningMessage;
                return data.errorMessage || 'Could Not Complete';
              })()}
            </Typography>

            {data.isSuccess ? (
              <CircleCheck
                role="img"
                aria-label="Sucessful Notification"
                size="1.2rem"
                color={whiteColor}
                fill={successColor}
              >
                <title>Sucessful Notification</title>
              </CircleCheck>
            ) : (
              <CircleAlert
                role="img"
                aria-label="Warning Notification"
                size="1.2rem"
                color={whiteColor}
                className={handleGetCurrentFillStatus()}
              >
                <title>Warning Notification</title>
              </CircleAlert>
            )}
          </div>

          <div>{data.children}</div>
        </>
      ) : (
        <ProgressBar width={displayedCount} color={handleGetCurrentBgColor()} />
      )}
    </div>
  );
};
